import { isTrpcExecutionDeadlineError } from '@client/trpc/sessionHandling';
import {
    categoriseNetworkError,
    getHttpStatus,
    isNetworkError,
    isServerError,
} from '@client/utils/errors/network-error-detection';
import { debug } from '@client/utils/logger';

/**
 * Get title for server errors based on HTTP status
 */
function getServerErrorTitle(error: unknown): string {
    const httpStatus = getHttpStatus(error);

    switch (httpStatus) {
        case 500:
            return 'Server Error';
        case 502:
            return 'Service Temporarily Unavailable';
        case 503:
            return 'Service Unavailable';
        case 504:
            return 'Request Timeout';
        // 524 is Cloudflare's origin-timeout: the backend did not respond in time and the
        // edge returns an HTML page that fails JSON parsing. Surface it as a transient outage.
        // Sentence case per the UI-copy rule (the legacy 502/503/504 titles above predate it).
        case 524:
            return 'Service temporarily unavailable';
        default:
            return 'Server Error';
    }
}

export function extractErrorTitle(error: unknown | unknown[] | undefined): string | undefined {
    if (!error) return 'An Error Occurred';
    if (isTrpcExecutionDeadlineError(error)) return 'Request timed out';

    // Handle network errors first
    if (isNetworkError(error)) {
        const networkErrorDetails = categoriseNetworkError(error);
        return networkErrorDetails.title;
    }

    // Handle server errors (5xx HTTP status)
    if (isServerError(error)) {
        return getServerErrorTitle(error);
    }

    // Handle array of errors
    if (Array.isArray(error)) {
        debug('[errorTitleExtraction] Error is an array for title extraction:', error);

        if (error.length === 0) {
            return 'No Errors';
        }

        if (error.length === 1) {
            // If there's only one error, extract title from it
            return extractErrorTitle(error[0]);
        }

        // Multiple errors - check if they're all validation errors
        const allValidationErrors = error.every((singleError) => {
            const title = extractErrorTitle(singleError);
            if (!title) return false;
            return title.includes('Validation') || title.includes('Form Validation');
        });

        if (allValidationErrors) {
            return 'Multiple Validation Errors';
        }

        return `Multiple Errors (${error.length})`;
    }

    // Handle TRPCClientError with JSON message containing Zod issues
    if (error instanceof Error) {
        debug('[errorTitleExtraction] Error is instance of Error, message:', error.message);

        try {
            const parsedMessage = JSON.parse(error.message);
            if (Array.isArray(parsedMessage)) {
                debug('[errorTitleExtraction] Found JSON array in error message:', parsedMessage);

                // Check if it looks like Zod issues
                const isZodIssues = parsedMessage.every(
                    (item) =>
                        item &&
                        typeof item === 'object' &&
                        'path' in item &&
                        'message' in item &&
                        Array.isArray(item.path),
                );

                if (isZodIssues) {
                    debug('[errorTitleExtraction] Detected Zod issues from JSON error message');
                    return 'Form Validation Failed';
                }
            }
        } catch {
            // Not JSON, continue with other checks
            debug('[errorTitleExtraction] Error message is not valid JSON');
        }
    }

    // Handle structured error objects with validation data
    if (error && typeof error === 'object' && 'data' in error && error.data && typeof error.data === 'object') {
        debug('[errorTitleExtraction] Found structured error with data:', error.data);

        // Check for zodError
        if ('zodError' in error.data && error.data.zodError && typeof error.data.zodError === 'object') {
            const zodError = error.data.zodError;
            debug('[errorTitleExtraction] Found zodError:', zodError);

            // Handle the issues format (newer Zod format)
            if ('issues' in zodError && Array.isArray(zodError.issues)) {
                debug('[errorTitleExtraction] Found issues format in zodError');
                return 'Form Validation Failed';
            }

            // Handle the fieldErrors format (older format)
            if ('fieldErrors' in zodError && zodError.fieldErrors && typeof zodError.fieldErrors === 'object') {
                debug('[errorTitleExtraction] Found fieldErrors format in zodError');
                return 'Form Validation Failed';
            }
        }

        // Check if the error itself contains issues directly
        if ('issues' in error.data && Array.isArray(error.data.issues)) {
            debug('[errorTitleExtraction] Found issues directly in error.data');
            return 'Form Validation Failed';
        }

        // Check for specific error messages or codes in the data
        if ('message' in error.data && typeof error.data.message === 'string') {
            const message = error.data.message.toLowerCase();
            if (message.includes('validation')) return 'Validation Error';
            if (message.includes('permission') || message.includes('unauthorized')) return 'Permission Denied';
            if (message.includes('not found')) return 'Resource Not Found';
            if (message.includes('conflict') || message.includes('already exists')) return 'Conflict Error';
            if (message.includes('rate limit')) return 'Too Many Requests';
        }
    }

    // Handle standard Error objects
    if (error instanceof Error) {
        const message = error.message.toLowerCase();
        if (message.includes('validation')) return 'Validation Error';
        if (message.includes('network') || message.includes('fetch')) return 'Connection Error';
        if (message.includes('timeout')) return 'Request Timeout';
        if (message.includes('permission') || message.includes('unauthorized')) return 'Permission Denied';
        if (message.includes('not found')) return 'Not Found';
        if (message.includes('conflict')) return 'Conflict Error';

        return 'Request failed';
    }

    // Handle string errors
    if (typeof error === 'string') {
        const message = error.toLowerCase();
        if (message.includes('validation')) return 'Validation Error';
        if (message.includes('network') || message.includes('connection')) return 'Connection Error';
        if (message.includes('permission') || message.includes('unauthorized')) return 'Permission Denied';
        if (message.includes('not found')) return 'Not Found';

        return 'Request failed';
    }

    return undefined;
}
